import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, RefreshCw, Save } from "lucide-react";
import middleApiService from "../api/middleApiService";
import { API_BASE_URL } from "../../../config";

// --- Enum-like helpers straight from the service ------------------------------
const { HomeResource, ParentResponse, SchoolSupportArea } = middleApiService;

// Home-resource tiles (label + emoji for nice visuals)
const RESOURCE_OPTIONS = [
    { id: HomeResource.BOOKS_MAGAZINES, label: "Books and Magazines", emoji: "📚" },
    { id: HomeResource.NEWSPAPERS, label: "Newspapers", emoji: "📰" },
    { id: HomeResource.TOYS_GAMES_SPORTS, label: "Toys, Games and sports", emoji: "🧸" },
    { id: HomeResource.PHONE_COMPUTER, label: "Phone and Computer", emoji: "💻" },
    { id: HomeResource.INTERNET, label: "Internet", emoji: "🌐" },
    { id: HomeResource.PUBLIC_BROADCAST, label: "Public Broadcast System", emoji: "📻" },
    { id: HomeResource.CWSN_RESOURCES, label: "Resources for CWSN", emoji: "♿" },
];

// Understanding of my child statements
const UNDERSTANDING_QUESTIONS = [
    {
        id: "motivated_to_learn",
        text: "My child seems motivated to learn and engage with new concepts learnt at school."
    },
    {
        id: "follows_schedule",
        text: "My child follows a schedule at home that includes curriculum and other activities, social connectivity, and screen time."
    },
    {
        id: "finds_curriculum_difficult",
        text: "My child finds the grade-level curriculum difficult and needs additional support.",
        reverse: true
    },
    {
        id: "making_good_progress",
        text: "My child is making good progress as per his/her grade."
    }
];

// Support needs check-list
const SUPPORT_OPTIONS = [
    { id: SchoolSupportArea.LANGUAGES, label: "Languages (R1, R2, R3)" },
    { id: SchoolSupportArea.MATHEMATICS, label: "Mathematics" },
    { id: SchoolSupportArea.SCIENCE, label: "Science" },
    { id: SchoolSupportArea.SOCIAL_SCIENCE, label: "Social Science" },
    { id: SchoolSupportArea.SELF_BELIEF_RELIANCE, label: "Building self-belief & self-reliance" },
    { id: SchoolSupportArea.SOCIAL_SKILLS_CONFLICT, label: "Developing social skills & conflict resolution" },
    { id: SchoolSupportArea.DIFFICULT_EMOTIONS_ANGER, label: "Managing difficult emotions like anger" },
    { id: SchoolSupportArea.STUDY_SKILLS_TIME, label: "Developing effective study skills like time management" },
    { id: SchoolSupportArea.DIGITAL_LITERACY, label: "Skill Guidance/Digital Literacy" }
];

// Fixed display order for the 4 response buttons
const RESPONSE_ORDER = [
    ParentResponse.YES,
    ParentResponse.SOMETIMES,
    ParentResponse.NO,
    ParentResponse.NOT_SURE,
];

// Turn a response value into its (possibly reversed) emoji pair
const emojiFor = (resp, reverse = false) => {
    const base = {
        [ParentResponse.YES]: "😊👍",
        [ParentResponse.SOMETIMES]: "😐👉",
        [ParentResponse.NO]: "😔👎",
        [ParentResponse.NOT_SURE]: "❓",
    };
    if (!reverse) return base[resp];
    // Swap YES/NO when statement is negatively worded
    return resp === ParentResponse.YES
        ? base[ParentResponse.NO]
        : resp === ParentResponse.NO
            ? base[ParentResponse.YES]
            : base[resp];
};

/**
 * Middle Stage Caregiver Observation Component
 * Implements both edit and view-only modes for the parent-teacher partnership card
 *
 * @param {string} childId - The child's ID
 * @param {string} assignmentId - The assignment ID
 * @param {Object} submission - The submission object (if viewing)
 * @param {Function} onSubmitComplete - Callback after successful submission
 */
export default function MiddleStageCaregiverObservation({
    childId,
    assignmentId: propAssignmentId,
    submission,
    onSubmitComplete,
}) {
    // Prefer explicit prop; fall back to submission object if present
    const assignmentId = propAssignmentId ?? submission?.assignment_id ?? null;

    /* ------------------------- local form state ------------------------ */
    const [form, setForm] = useState({
        resources: [],
        other_resources: "",
        understanding: UNDERSTANDING_QUESTIONS.reduce(
            (acc, q) => ({ ...acc, [q.id]: ParentResponse.NOT_SURE }),
            {}
        ),
        school_support_needs: [],
        other_school_support: "",
        home_support_plan: ""
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [retrying, setRetrying] = useState(false);

    // Load existing submission data if available
    useEffect(() => {
        if (submission?.caregiver_observation) {
            try {
                const obs = submission.caregiver_observation;
                setForm({
                    resources: obs.resources || [],
                    other_resources: obs.other_resources || "",
                    understanding: obs.understanding || UNDERSTANDING_QUESTIONS.reduce(
                        (acc, q) => ({ ...acc, [q.id]: ParentResponse.NOT_SURE }),
                        {}
                    ),
                    school_support_needs: obs.school_support_needs || [],
                    other_school_support: obs.other_school_support || "",
                    home_support_plan: obs.home_support_plan || ""
                });
                console.log("Loaded caregiver observation data:", obs);
            } catch (err) {
                console.error("Error loading caregiver observation data:", err);
                setError("Failed to load existing feedback. You can try submitting again.");
            }
        }
    }, [submission]);

    /* ----------------------- little state helpers ---------------------- */
    const toggle = (arr, v) =>
        arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

    const handleResource = (id) =>
        setForm((f) => ({ ...f, resources: toggle(f.resources, id) }));

    const handleSupport = (id) =>
        setForm((f) => ({ ...f, school_support_needs: toggle(f.school_support_needs, id) }));

    const setUnderstanding = (id, resp) =>
        setForm((f) => ({
            ...f,
            understanding: { ...f.understanding, [id]: resp },
        }));

    const handleChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    // Handle direct API submission with fallback
    const submitWithFallback = async () => {
        if (!childId || !assignmentId) {
            throw new Error("Child ID and Assignment ID are required");
        }

        // First try the normal API
        try {
            console.log("Trying middle stage API first");
            const result = await middleApiService.addCaregiverObservation(
                childId,
                assignmentId,
                form
            );
            return result;
        } catch (error) {
            console.warn("Middle stage API failed:", error);

            // If the API fails, try the direct approach with fetch
            console.log("Falling back to direct fetch approach");

            const accessToken = localStorage.getItem("accessToken");
            if (!accessToken) {
                throw new Error("No access token found. Please log in again.");
            }

            // Prepare the payload in the format expected by the API
            const payload = {
                resources: Array.isArray(form.resources) ? form.resources : [],
                other_resources: form.other_resources?.trim() || "",
                understanding: {
                    motivated_to_learn: form.understanding?.motivated_to_learn || ParentResponse.NOT_SURE,
                    follows_schedule: form.understanding?.follows_schedule || ParentResponse.NOT_SURE,
                    finds_curriculum_difficult: form.understanding?.finds_curriculum_difficult || ParentResponse.NOT_SURE,
                    making_good_progress: form.understanding?.making_good_progress || ParentResponse.NOT_SURE
                },
                school_support_needs: Array.isArray(form.school_support_needs) ? form.school_support_needs : [],
                other_school_support: form.other_school_support?.trim() || "",
                home_support_plan: form.home_support_plan?.trim() || ""
            };

            console.log("Direct fetch payload:", payload);

            const response = await fetch(
                `${API_BASE_URL}/middle/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-observation`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                // Try foundational API as a last resort
                console.warn(`Middle stage API failed with status ${response.status}, trying foundational API as last resort`);

                // Adjust payload for foundational API
                const foundationalPayload = {
                    resources: payload.resources,
                    comments: payload.home_support_plan || "Submitted via Middle Stage form"
                };

                const foundationalResponse = await fetch(
                    `${API_BASE_URL}/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-feedback`,
                    {
                        method: "POST",
                        headers: {
                            "Authorization": `Bearer ${accessToken}`,
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify(foundationalPayload)
                    }
                );

                if (!foundationalResponse.ok) {
                    const errorData = await foundationalResponse.json();
                    throw new Error(errorData.detail || "Failed to submit feedback using all methods");
                }

                return await foundationalResponse.json();
            }

            return await response.json();
        }
    };

    /* ----------------------------- submit ------------------------------ */
    const submit = async (e) => {
        e.preventDefault();

        // Validation
        if (!childId) {
            setError("Child ID is required.");
            return;
        }

        if (!assignmentId) {
            setError("Assignment ID is required to submit feedback.");
            return;
        }

        // Check if at least one understanding question is answered (not NOT_SURE)
        const hasAnswers = Object.values(form.understanding).some(
            response => response !== ParentResponse.NOT_SURE
        );

        if (!hasAnswers) {
            setError("Please answer at least one question about your understanding of your child.");
            return;
        }

        // Validate home support plan
        if (!form.home_support_plan.trim()) {
            setError("Please provide information about how you will support your child at home.");
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setSuccess(false);

        try {
            console.log("Submitting caregiver observation:", {
                childId,
                assignmentId,
                formData: form
            });

            const result = await submitWithFallback();

            console.log("Submission successful:", result);
            setSuccess(true);

            // Call parent callback after successful submission
            if (onSubmitComplete) {
                onSubmitComplete(result);
            }
        } catch (err) {
            console.error("Submission error:", err);
            setError(err.message || "Failed to submit caregiver observation. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handler for retry button
    const handleRetry = () => {
        setRetrying(true);
        setError(null);

        // Simulate a delay then try again
        setTimeout(() => {
            submit({
                preventDefault: () => {
                }
            });
            setRetrying(false);
        }, 1000);
    };

    /* ----------------- reusable row for each statement ---------------- */
    const UnderstandingRow = ({ q, viewOnly }) => {
        try {
            // Add proper null checks and fallbacks to fix the error
            const understandingData = submission?.caregiver_observation?.understanding || {};

            const selected = viewOnly
                ? understandingData[q.id] || ParentResponse.NOT_SURE
                : form.understanding[q.id];

            return (
                <>
                    {/* Emoji buttons row */}
                    <tr className="border-b border-orange-200 bg-orange-50">
                        <td
                            className="w-2/5 px-4 py-3 text-sm font-medium align-top"
                        >
                            {q.text}
                        </td>

                        {RESPONSE_ORDER.map((resp) => (
                            <td key={resp}
                                className={`px-3 py-3 text-center ${selected === resp ? 'bg-orange-100' : ''}`}>
                                {viewOnly ? (
                                    <span
                                        className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xl ${selected === resp ? "ring-2 ring-orange-600" : ""
                                            }`}
                                    >
                                        {emojiFor(resp, q.reverse)}
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setUnderstanding(q.id, resp)}
                                        className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xl transition hover:bg-orange-100 ${selected === resp
                                                ? "bg-orange-200 ring-2 ring-orange-600"
                                                : "bg-white"
                                            }`}
                                    >
                                        {emojiFor(resp, q.reverse)}
                                    </button>
                                )}
                            </td>
                        ))}
                    </tr>
                </>
            );
        } catch (err) {
            console.error("Error rendering understanding row:", err);
            return (
                <tr className="border-b border-red-200 bg-red-50">
                    <td colSpan={5} className="px-4 py-3 text-sm text-red-500">
                        Error rendering this row: {err.message}
                    </td>
                </tr>
            );
        }
    };

    /* ========================== READ-ONLY CARD ========================= */
    if (submission?.caregiver_observation) {
        try {
            const obs = submission.caregiver_observation;

            return (
                <section
                    className="font-[poppins] bg-white border border-gray-200 shadow rounded-lg overflow-hidden mb-6">
                    <header className="p-6 text-center">
                        <h2 className="text-2xl font-bold text-orange-600">
                            Parent-Teacher Partnership Card
                        </h2>
                        <p className="text-gray-600 mt-1">
                            Submitted on{" "}
                            {new Date(obs.created_at || new Date()).toLocaleDateString()}
                        </p>
                    </header>

                    {/* ➜ Resources */}
                    <div className="border-t border-gray-200 px-6 py-4">
                        <h3 className="text-lg font-semibold mb-4">
                            Resources available to your child at home
                        </h3>

                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                            {RESOURCE_OPTIONS.map((r) => (
                                <div
                                    key={r.id}
                                    className={`flex flex-col items-center p-2 rounded-lg ${(obs.resources ?? []).includes(r.id) ? "bg-green-50" : "bg-gray-50"
                                        }`}
                                >
                                    <div
                                        className={`h-16 w-16 flex items-center justify-center rounded-lg text-4xl mb-2 ${(obs.resources ?? []).includes(r.id)
                                                ? "border-2 border-green-500"
                                                : "border"
                                            }`}
                                    >
                                        {r.emoji}
                                    </div>
                                    <span className="text-xs text-center font-medium">
                                        {r.label}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {obs.other_resources && (
                            <p className="mt-3 text-sm bg-gray-50 p-2 rounded">
                                <span className="font-semibold">Other: </span>
                                {obs.other_resources}
                            </p>
                        )}
                    </div>

                    {/* ➜ Understanding of my Child */}
                    <div className="mt-6">
                        <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                            Understanding of my Child
                        </div>
                        <table className="min-w-full border border-orange-200">
                            <thead>
                                <tr className="bg-orange-500 text-white text-xs uppercase tracking-wide">
                                    <th className="w-2/5 px-4 py-1 text-left border-r border-orange-300">Statement</th>
                                    {RESPONSE_ORDER.map((resp) => (
                                        <th key={resp}
                                            className="px-4 py-1 text-center border-r border-orange-300 last:border-r-0">{resp}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {UNDERSTANDING_QUESTIONS.map((q) => (
                                    <UnderstandingRow key={q.id} q={q} viewOnly={true} />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ➜ Support needs */}
                    <div className="mt-6">
                        <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                            At school, my child needs support with:
                        </div>
                        <div className="border border-t-0 border-orange-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {SUPPORT_OPTIONS.map((s) => (
                                <div key={s.id} className="flex items-center">
                                    {(obs.school_support_needs ?? []).includes(s.id) && (
                                        <svg
                                            className="w-4 h-4 mr-2 text-green-600"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    )}
                                    <span
                                        className={
                                            (obs.school_support_needs ?? []).includes(s.id)
                                                ? "font-medium"
                                                : "text-gray-500"
                                        }
                                    >
                                        {s.label}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {obs.other_school_support && (
                            <div className="border border-t-0 border-orange-200 p-4">
                                <p className="text-sm">
                                    <span className="font-semibold">Other: </span>
                                    {obs.other_school_support}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* ➜ Home support plan */}
                    <div className="mt-6">
                        <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                            Based on my discussion with the teacher, I will support my child at home by:
                        </div>
                        <div className="border border-t-0 border-orange-200 p-4 bg-orange-50">
                            <p className="text-sm whitespace-pre-wrap">{obs.home_support_plan}</p>
                        </div>
                        <div className="border border-t-0 border-orange-200 p-2 text-xs italic text-gray-500">
                            <p>
                                (Fostering a strong parent-teacher partnership is essential to ensure your child's
                                holistic development. While teachers support at school, please use this space to write
                                how can you provide additional support to your child at home.)
                            </p>
                        </div>
                    </div>
                </section>
            );
        } catch (error) {
            console.error("Error rendering read-only view:", error);
            return (
                <div
                    className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-red-200">
                    <div className="px-4 py-3 bg-red-100">
                        <h2 className="text-lg font-medium text-center uppercase">Parent-Teacher Partnership Card
                            (Error)</h2>
                    </div>
                    <div className="p-6">
                        <p className="text-red-500 mb-2">Something went wrong when displaying your feedback.</p>
                        <p className="mb-4">Your feedback has been saved, but we're having trouble displaying it.</p>
                        <details>
                            <summary className="text-sm cursor-pointer text-gray-500">Technical details</summary>
                            <pre className="text-xs bg-gray-100 p-2 mt-2 rounded overflow-auto">{error.toString()}</pre>
                        </details>
                    </div>
                </div>
            );
        }
    }

    /* ============================ EDIT FORM ============================ */
    return (
        <form
            onSubmit={submit}
            className="font-[poppins] bg-white border border-gray-200 shadow rounded-lg overflow-hidden mb-6"
        >
            <header className="p-6 text-center">
                <h2 className="text-2xl font-bold text-orange-600">
                    Parent-Teacher Partnership Card
                </h2>
                <p className="text-gray-600 mt-1">
                    Help us understand how we can better support your child.
                </p>
            </header>

            {/* Success message */}
            {success && (
                <div className="mx-6 mb-4 flex items-center bg-green-100 text-green-800 p-3 rounded">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Feedback submitted successfully! Thank you for your input.
                </div>
            )}

            {/* ➜ Home resources (icons + checkboxes) */}
            <section className="border-t border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold mb-4">
                    Tick the resources available to your child at home.
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-5">
                    {RESOURCE_OPTIONS.map((r) => (
                        <div key={r.id} className="flex flex-col items-center">
                            <button
                                type="button"
                                onClick={() => handleResource(r.id)}
                                className={`h-16 w-16 flex items-center justify-center rounded-lg text-4xl mb-2 transition ${form.resources.includes(r.id)
                                        ? "bg-orange-200 border-2 border-orange-500"
                                        : "bg-gray-100 hover:bg-orange-50"
                                    }`}
                            >
                                {r.emoji}
                            </button>
                            <span className="text-sm text-center font-medium mb-1">
                                {r.label}
                            </span>
                            <input
                                type="checkbox"
                                checked={form.resources.includes(r.id)}
                                onChange={() => handleResource(r.id)}
                                className="h-4 w-4 text-orange-600 border-gray-300 rounded"
                            />
                        </div>
                    ))}
                </div>

                <div className="mt-4">
                    <label
                        htmlFor="other_resources"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Any other (please specify)
                    </label>
                    <input
                        id="other_resources"
                        name="other_resources"
                        value={form.other_resources}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Specify other resources available at home"
                    />
                </div>
            </section>

            {/* ➜ Understanding of my Child */}
            <section className="mt-6">
                <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                    Understanding of my Child
                    <p className="text-orange-100 text-sm font-normal">
                        Circle the most appropriate option for your child.
                    </p>
                </div>

                <table className="min-w-full border border-orange-200">
                    <thead>
                        <tr className="bg-orange-500 text-white text-xs uppercase tracking-wide">
                            <th className="w-2/5 px-4 py-1 text-left border-r border-orange-300">Statement</th>
                            {RESPONSE_ORDER.map((resp) => (
                                <th key={resp}
                                    className="px-4 py-1 text-center border-r border-orange-300 last:border-r-0">{resp}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {UNDERSTANDING_QUESTIONS.map((q) => (
                            <UnderstandingRow key={q.id} q={q} viewOnly={false} />
                        ))}
                    </tbody>
                </table>
            </section>

            {/* ➜ School support needs */}
            <section className="mt-6">
                <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                    At school, my child needs support with:
                </div>

                <div className="border border-t-0 border-orange-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {SUPPORT_OPTIONS.map((s) => (
                        <label key={s.id} className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-orange-600 border-gray-300 rounded"
                                checked={form.school_support_needs.includes(s.id)}
                                onChange={() => handleSupport(s.id)}
                            />
                            <span className="text-sm">{s.label}</span>
                        </label>
                    ))}
                </div>

                <div className="border border-t-0 border-orange-200 p-4">
                    <label
                        htmlFor="other_school_support"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Any other (please specify)
                    </label>
                    <input
                        id="other_school_support"
                        name="other_school_support"
                        value={form.other_school_support}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Specify other areas where your child needs support"
                    />
                </div>
            </section>

            {/* ➜ Home support plan */}
            <section className="mt-6">
                <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                    Based on my discussion with the teacher, I will support my child at home by:
                </div>

                <div className="border border-t-0 border-orange-200 p-4">
                    <textarea
                        id="home_support_plan"
                        name="home_support_plan"
                        value={form.home_support_plan}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Describe how you will support your child at home"
                        required
                    ></textarea>

                    <p className="mt-2 text-xs italic text-gray-500">
                        Fostering a strong parent-teacher partnership is essential to ensure your child's holistic
                        development.
                        While teachers support at school, please use this space to write how can you provide additional
                        support to your child at home.
                    </p>
                </div>
            </section>

            {/* ➜ Submit / error bar */}
            <div className="border-t border-gray-200 px-6 py-4">
                {error && (
                    <div className="flex items-center bg-red-100 text-red-800 p-3 rounded mb-4">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        <div className="flex-1">
                            <p>{error}</p>
                            {error.includes("Failed to submit") && (
                                <button
                                    type="button"
                                    onClick={handleRetry}
                                    disabled={retrying || isSubmitting}
                                    className="mt-2 flex items-center text-sm font-medium text-red-700 hover:text-red-900"
                                >
                                    <RefreshCw size={16} className={`mr-1 ${retrying ? 'animate-spin' : ''}`} />
                                    {retrying ? 'Retrying...' : 'Try Again'}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSubmitting || retrying}
                        className="inline-flex items-center px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded shadow font-medium transition-colors"
                    >
                        {isSubmitting || retrying ? (
                            <svg
                                className="animate-spin h-5 w-5 mr-3"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : (
                            <Save size={20} className="mr-2" />
                        )}
                        {isSubmitting ? "Saving..." : retrying ? "Retrying..." : "Submit Feedback"}
                    </button>
                </div>
            </div>
        </form>
    );
}